import React from "react";
import { Eye, Layers, Lock, Wallet } from "lucide-react";
import {
  Affix,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Field,
  Input,
  KV,
  ToggleRow,
} from "../components/primitives";

const RedemptionTab = ({ config, setConfig, readOnly }) => {
  if (!config) return null;
  const redeem = config.redeem_config || {};
  const set = (k, v) => setConfig({ ...config, redeem_config: { ...redeem, [k]: v } });

  const examplePts = 2840;
  const exampleValue = examplePts * (redeem.pt_value || 0);

  const policy = redeem.benefit_policy || "two";
  const setPolicy = (v) => set("benefit_policy", v);

  const policies = [
    { id: "one", title: "Only one offer", desc: "Customer may use a coupon OR a scheme OR points — one per bill." },
    { id: "two", title: "Any two offers", desc: "Allow any two of the three to combine on a bill." },
    { id: "all", title: "All three offers", desc: "Coupon + scheme + points may all apply together." },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-[17px] font-bold text-[#0F172A]">Redemption</h2>
        <p className="text-[13px] text-[#64748B] mt-1 max-w-[700px]">
          Set what loyalty points are worth at checkout and the guardrails on how they can be redeemed against a bill.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        <div className="space-y-4">
          <Card>
            <CardHeader
              icon={<Wallet size={18} />}
              tone="green"
              title="Conversion rate"
              desc="The cash value of one point"
            />
            <CardBody>
              <div className="flex items-end gap-3 flex-wrap">
                <Field label="1 point is worth" style={{ width: 160 }}>
                  <Affix pre="₹">
                    <Input
                      type="number"
                      step="0.05"
                      value={redeem.pt_value ?? 0.25}
                      onChange={(e) => set("pt_value", Number(e.target.value))}
                      style={{ paddingLeft: 28 }}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
                <div className="pb-[10px] text-[12px] font-semibold text-[#64748B]">
                  at the time of billing
                </div>
              </div>
              <div className="mt-4">
                <Callout tone="blue">
                  <b>100 pts = ₹{(100 * (redeem.pt_value || 0)).toFixed(2)}.</b> This rate is locked at redemption — later changes don't affect already-issued points.
                </Callout>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader icon={<Lock size={18} />} title="Redemption limits" desc="Guardrails applied at checkout" />
            <CardBody>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Minimum points to redeem" help="Members can't redeem below this balance.">
                  <Affix post="pts">
                    <Input
                      type="number"
                      value={redeem.min_redeem ?? 100}
                      onChange={(e) => set("min_redeem", Number(e.target.value))}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
                <Field label="Redeem in multiples of" help="e.g. only in blocks of 50 points.">
                  <Affix post="pts">
                    <Input
                      type="number"
                      value={redeem.multiple_of ?? 50}
                      onChange={(e) => set("multiple_of", Number(e.target.value))}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
                <Field label="Max % of bill payable by points" help="Caps how much of a single bill points can cover.">
                  <Affix post="%">
                    <Input
                      type="number"
                      value={redeem.max_pct_of_bill ?? 30}
                      onChange={(e) => set("max_pct_of_bill", Number(e.target.value))}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
                <Field label="Min. bill to allow redemption" help="Points can only be used above this bill value.">
                  <Affix pre="₹">
                    <Input
                      type="number"
                      value={redeem.min_bill_to_redeem ?? 500}
                      onChange={(e) => set("min_bill_to_redeem", Number(e.target.value))}
                      style={{ paddingLeft: 28 }}
                      disabled={readOnly}
                    />
                  </Affix>
                </Field>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader icon={<Layers size={18} />} tone="purple" title="Stacking rules" />
            <CardBody className="py-1">
              <ToggleRow
                title="Cap redemption to nearest covered amount"
                desc="Prevents redeeming more points than the bill is worth."
                on={!!redeem.round_redeem}
                onChange={(v) => set("round_redeem", v)}
                disabled={readOnly}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              icon={<Layers size={18} />}
              tone="purple"
              title="Offer combination policy"
              desc="How many of Coupon / Scheme / Loyalty points may apply to one bill"
            />
            <CardBody>
              <div className="space-y-2">
                {policies.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => !readOnly && setPolicy(p.id)}
                    disabled={readOnly}
                    className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                      policy === p.id
                        ? "border-primary bg-primary/5"
                        : "border-[#E6E9EF] hover:bg-[#F8FAFC]"
                    } ${readOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
                  >
                    <span
                      className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center ${
                        policy === p.id ? "border-primary" : "border-[#CBD5E1]"
                      }`}
                    >
                      {policy === p.id && (
                        <span className="w-2 h-2 rounded-full bg-primary" />
                      )}
                    </span>
                    <span>
                      <span className="block text-[13px] font-semibold text-[#0F172A]">
                        {p.title}
                      </span>
                      <span className="block text-[12px] text-[#64748B] mt-0.5">{p.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader icon={<Eye size={18} />} tone="green" title="Redemption preview" />
            <CardBody>
              <Callout tone="green">
                <div className="text-[12px] font-semibold mb-1">
                  A member with {examplePts.toLocaleString("en-IN")} points can redeem
                </div>
                <div className="text-[28px] font-bold leading-none tracking-tight" style={{ color: "#15803C" }}>
                  ₹{exampleValue.toFixed(2)}
                </div>
              </Callout>
              <div className="pt-3 space-y-0">
                <KV
                  label="On a ₹2,000 bill"
                  value={`max ₹${(2000 * (redeem.max_pct_of_bill || 0) / 100).toFixed(2)}`}
                />
                <KV
                  label="Points needed for that"
                  value={`${Math.floor((2000 * (redeem.max_pct_of_bill || 0) / 100) / (redeem.pt_value || 1))} pts`}
                />
                <KV label="Min. balance" value={`${redeem.min_redeem || 0} pts`} />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RedemptionTab;
